import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  IconButton,
  Paper,
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AlterarServicoModal = ({ 
  open, 
  onClose, 
  senha,
  onConfirm,
  customTheme,
  TransitionComponent,
  Backdrop 
}) => {
  const [servicos, setServicos] = useState([]);
  const [servicoSelecionado, setServicoSelecionado] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [carregandoServicos, setCarregandoServicos] = useState(false);
  const [erro, setErro] = useState('');

  // Buscar serviços disponíveis
  useEffect(() => {
    if (open) {
      buscarServicos();
    }
  }, [open]);

  const buscarServicos = async () => {
    try {
      setCarregandoServicos(true);
      setErro('');
      
      const response = await api.get('/servicos');
      
      if (response.data.success) {
        setServicos(response.data.data);
      } else {
        setErro('Erro ao buscar serviços disponíveis');
      }
    } catch (error) {
      console.error('Erro ao buscar serviços:', error);
      setErro('Erro ao comunicar com o servidor');
    } finally {
      setCarregandoServicos(false);
    }
  };

  const handleConfirmar = async () => {
    if (!servicoSelecionado) {
      toast.warning('Por favor, selecione um serviço');
      return;
    }

    if (servicoSelecionado === (senha?.servico_nome || senha?.tipo)) {
      toast.warning('O serviço selecionado é o mesmo atual da senha');
      return;
    }

    // Apenas passar o nome do serviço para o handler pai
    onConfirm && onConfirm(servicoSelecionado);
    handleClose();
  };

  const handleClose = () => {
    setServicoSelecionado('');
    setErro('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={TransitionComponent}
      TransitionProps={{
        timeout: {
          enter: 400,
          exit: 300
        }
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: alpha(customTheme?.palette?.common?.black || '#000', 0.5),
          backdropFilter: 'blur(2px)',
          transition: 'backdrop-filter 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(customTheme?.palette?.common?.black || '#000', 0.2)}`,
          maxWidth: 500,
          mx: 2,
          mb: { xs: 1, sm: 4 },
          overflowY: 'auto',
          transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1) !important',
          '&.MuiDialog-paper': {
            transform: open ? 'translateY(0) !important' : 'translateY(100%) !important'
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SwapHorizIcon sx={{ color: customTheme?.palette?.primary?.main || '#1976d2', mr: 1.5 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Alterar Serviço da Senha
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={handleClose} 
          aria-label="fechar"
          sx={{ 
            color: customTheme?.palette?.grey?.[500] || '#757575',
            '&:hover': {
              color: customTheme?.palette?.grey?.[800] || '#424242',
              bgcolor: alpha(customTheme?.palette?.grey?.[500] || '#757575', 0.1)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Informações da senha atual */}
        {senha && (
          <Paper elevation={0} sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: alpha('#f5f5f5', 0.5), 
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.2)
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Senha Atual
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Número</Typography>
                <Chip 
                  label={senha.numero || senha.senha} 
                  color="primary" 
                  sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Serviço Atual</Typography>
                <Chip 
                  label={senha.servico_nome || senha.tipo} 
                  color="default" 
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* Alerta informativo */}
        <Alert 
          severity="info" 
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            <strong>Atenção:</strong> O número da senha permanecerá o mesmo, 
            apenas o serviço será alterado. A senha retornará para o início da fila.
          </Typography>
        </Alert>

        {/* Seleção de novo serviço */}
        {erro ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        ) : carregandoServicos ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="servico-select-label">
              Selecione o Novo Serviço
            </InputLabel>
            <Select
              labelId="servico-select-label"
              value={servicoSelecionado}
              label="Selecione o Novo Serviço"
              onChange={(e) => setServicoSelecionado(e.target.value)}
              disabled={carregando}
            >
              {servicos.map((servico) => (
                <MenuItem 
                  key={servico.id} 
                  value={servico.nome}
                  disabled={servico.nome === (senha?.servico_nome || senha?.tipo)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{servico.nome}</Typography>
                    {servico.nome === (senha?.servico_nome || senha?.tipo) && (
                      <Chip size="small" label="Atual" color="default" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Visualização da mudança */}
        {servicoSelecionado && servicoSelecionado !== (senha?.servico_nome || senha?.tipo) && (
          <Paper elevation={1} sx={{ 
            p: 2, 
            bgcolor: alpha('#4caf50', 0.05),
            border: '1px solid',
            borderColor: alpha('#4caf50', 0.3),
            borderRadius: 2 
          }}>
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              Prévia da Alteração
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={senha?.servico_nome || senha?.tipo} color="default" size="small" />
              <SwapHorizIcon color="success" />
              <Chip label={servicoSelecionado} color="success" size="small" />
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px'
            }
          }}
          disabled={carregando}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmar} 
          variant="contained" 
          color="primary"
          disabled={!servicoSelecionado || carregando || servicoSelecionado === (senha?.servico_nome || senha?.tipo)}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            boxShadow: `0 4px 12px ${alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.3)}`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(customTheme?.palette?.primary?.main || '#1976d2', 0.4)}`,
            }
          }}
        >
          {carregando ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Alterando...
            </Box>
          ) : (
            'Confirmar Alteração'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlterarServicoModal; 